import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Input from './Input';
import { Customer } from '../../models/customer/Customer';
import { getSingleCustomerBySearchParam } from '../../api/requests';
import { getFormattedPhoneNumber, isNumberValue } from '../../utils/miscFunctions';

interface Props {
  label?: string;
  name?: string;
  id?: string;
  value: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: any;
  helperText?: any;
  className?: string;
  labelClasses?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<any>) => void;
  onCustomerPicked: (customer: Customer) => void;
  /** Min chars before the typeahead fires. Defaults to 1 (prefix match from first letter). */
  minChars?: number;
  /** Debounce delay in ms. Defaults to 250. */
  debounceMs?: number;
}

const CustomerTypeaheadInput: React.FC<Props> = ({
  label,
  name,
  id,
  value,
  placeholder,
  required,
  disabled,
  error,
  helperText,
  className,
  labelClasses,
  onChange,
  onBlur,
  onCustomerPicked,
  minChars = 1,
  debounceMs = 250,
}) => {
  const [suggestions, setSuggestions] = useState<Customer[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);
  const skipNextSearchRef = useRef(false);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = (value || '').trim();
    if (trimmed.length < minChars) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      setLoading(true);
      try {
        const isPhone = isNumberValue(trimmed);
        const formatted = isPhone ? getFormattedPhoneNumber(trimmed) : trimmed;
        const response: any = await getSingleCustomerBySearchParam(formatted);
        if (seq !== seqRef.current) return;
        const list: Customer[] = (response?.data || []).map((c: any) => new Customer(c));
        setSuggestions(list);
        setOpen(true);
        setActiveIndex(list.length > 0 ? 0 : -1);
      } catch {
        if (seq !== seqRef.current) return;
        setSuggestions([]);
        setOpen(false);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, debounceMs);
  }, [value, minChars, debounceMs]);

  const handleSelect = (customer: Customer) => {
    skipNextSearchRef.current = true;
    setOpen(false);
    setSuggestions([]);
    onCustomerPicked(customer);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((idx) => (idx + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((idx) => (idx - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        type="text"
        name={name}
        id={id}
        label={label}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        className={className}
        labelClasses={labelClasses}
        autoComplete={false}
      />
      {(loading || (open && (suggestions.length > 0 || (!loading && (value || '').trim().length >= minChars)))) && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-darkBg border border-gray-200 dark:border-darkBg-border rounded-lg shadow-lg max-h-72 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading && suggestions.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500 dark:text-darkBg-subText">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          )}
          {!loading && suggestions.length === 0 && (value || '').trim().length >= minChars && (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-darkBg-subText">No matches</div>
          )}
          {suggestions.map((customer, idx) => {
            const fullName = `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim();
            const isActive = idx === activeIndex;
            return (
              <button
                type="button"
                key={`${customer.id}-${idx}`}
                className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 border-gray-100 dark:border-darkBg-border ${isActive ? 'bg-primary-surface dark:bg-darkBg-border' : 'hover:bg-primary-surface dark:hover:bg-darkBg-border'}`}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => handleSelect(customer)}
              >
                <div className="font-medium text-gray-900 dark:text-darkBg-mainText truncate">{fullName || 'Unnamed customer'}</div>
                <div className="text-xs text-gray-500 dark:text-darkBg-subText truncate">
                  {customer.phone || customer.email || '—'}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerTypeaheadInput;
